// ============================================================================
// The work rows (item #650; actions item #835).
//
// A row is tapped to open its card at the field. A swipe left reveals two
// actions in the slot "Not mine" used to have (Ben, 2026-10-13, option B):
//   Send to…  hand it to a person — prefilled with the building's account
//             manager, else the role that owns the row, else you pick.
//   Clear     take it off YOUR list only. The reason is optional.
// Each opens a sheet, and only the sheet's button acts. There is still no Done
// control: nothing here marks anything complete for anyone else.
// ============================================================================
import { seatWord } from '../../shared/role-gates';
import { useMemo, useRef, useState, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react';
import { overlayClickClose } from '../../shared/overlay-close';
import type { Band, Kind, WorkRow } from './home-work';
import type { Density, DoneItem } from './done-today';
import { sendToDefault, type Person } from './hand-off';

const KIND_STYLE: Record<Kind, string> = {
  Project: 'bg-st-warn-bg text-st-warn-fg',
  Complaint: 'bg-st-danger-bg text-st-danger-fg',
  'New account': 'bg-st-ok-bg text-st-ok-fg',
  Admin: 'bg-tcs-bg-muted text-tcs-text-secondary',
};

/** Two actions, 104 px each — the width of the one action they replace, twice. */
const SLOT = 104;
const REVEAL = SLOT * 2;

export function WorkRowView({
  row,
  density,
  onOpen,
  onSendTo,
  onClear,
}: {
  row: WorkRow;
  density: Density;
  onOpen: (r: WorkRow) => void;
  onSendTo: (r: WorkRow) => void;
  onClear: (r: WorkRow) => void;
}) {
  const [dx, setDx] = useState(0);
  const start = useRef<{ x: number; y: number; base: number } | null>(null);
  const dragged = useRef(false);
  const compact = density === 'compact';

  const down = (e: ReactPointerEvent) => {
    start.current = { x: e.clientX, y: e.clientY, base: dx };
    dragged.current = false;
  };
  const move = (e: ReactPointerEvent) => {
    if (!start.current) return;
    const ddx = e.clientX - start.current.x;
    if (Math.abs(ddx) > 8 && Math.abs(ddx) > Math.abs(e.clientY - start.current.y)) dragged.current = true;
    if (dragged.current) setDx(Math.max(-REVEAL, Math.min(0, start.current.base + ddx)));
  };
  const up = () => {
    if (!start.current) return;
    start.current = null;
    setDx((v) => (v < -REVEAL / 2 ? -REVEAL : 0));
  };

  const age = row.ageDays == null ? '—' : `${row.ageDays}d`;
  return (
    <div className="relative overflow-hidden border-b border-tcs-border last:border-b-0" style={{ touchAction: 'pan-y' }}>
      {/* Mounted only while the row is pulled open: left mounted under a resting
          row, its colour bled through the row's anti-aliased edge as a thin line. */}
      {dx < 0 && (
        <div className="absolute inset-y-0 right-0 flex items-stretch" style={{ width: REVEAL }}>
          <button
            type="button"
            tabIndex={dx === 0 ? -1 : 0}
            onClick={() => {
              setDx(0);
              onSendTo(row);
            }}
            style={{ width: SLOT }}
            className="bg-tcs-accent-ink text-[13px] font-bold text-tcs-accent-on"
          >
            Send to…
          </button>
          <button
            type="button"
            tabIndex={dx === 0 ? -1 : 0}
            onClick={() => {
              setDx(0);
              onClear(row);
            }}
            style={{ width: SLOT }}
            className="bg-tcs-inverse text-[13px] font-bold text-tcs-inverse-ink"
          >
            Clear
          </button>
        </div>
      )}
      <button
        type="button"
        onPointerDown={down}
        onPointerMove={move}
        onPointerUp={up}
        onPointerCancel={up}
        onClick={() => {
          if (dragged.current) {
            dragged.current = false;
            return;
          }
          if (dx !== 0) {
            setDx(0);
            return;
          }
          onOpen(row);
        }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowLeft') setDx(-REVEAL);
          if (e.key === 'ArrowRight') setDx(0);
        }}
        aria-label={`${row.kind}: ${row.name}. ${row.missing}`}
        className={[
          'relative flex w-full min-h-[44px] items-start gap-2 bg-tcs-bg px-3 text-left transition-transform duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-tcs-text motion-reduce:transition-none',
          compact ? 'py-2' : 'py-2.5',
        ].join(' ')}
        style={{ transform: `translateX(${dx}px)` }}
      >
        <span className="min-w-0 flex-1">
          <span className={`block text-[13.5px] text-tcs-text ${compact ? 'font-medium' : 'font-semibold'}`}>
            <span className={`mr-1.5 inline-block rounded px-1 py-px align-[1px] text-[9.5px] font-bold uppercase tracking-wide ${KIND_STYLE[row.kind]}`}>
              {row.kind}
            </span>
            {row.name}
          </span>
          {compact ? (
            <span className="block text-[12px] leading-snug">
              {row.context && <span className="text-tcs-text-secondary">{row.context} · </span>}
              <span className={row.ready ? 'font-semibold text-st-ok-fg' : 'font-semibold text-st-danger-fg'}>{row.missing}</span>
            </span>
          ) : (
            <>
              {row.context && <span className="mt-0.5 block text-[12.5px] text-tcs-text-secondary">{row.context}</span>}
              <span className={`mt-0.5 block text-[12.5px] font-semibold ${row.ready ? 'text-st-ok-fg' : 'text-st-danger-fg'}`}>{row.missing}</span>
            </>
          )}
        </span>
        <span className={`shrink-0 pt-0.5 text-[12px] tabular-nums ${row.ageDays != null && row.ageDays >= 143 ? 'font-semibold text-st-danger-fg' : 'text-tcs-text-muted'}`}>
          {age}
        </span>
      </button>
    </div>
  );
}

const BAND_LABEL: Record<Band, [string, string]> = {
  overdue: ['Overdue', 'must be done'],
  today: ['Today', 'new since yesterday'],
  this_week: ['This week', 'oldest first'],
};

export function BandList({
  band,
  rows,
  density,
  onOpen,
  onSendTo,
  onClear,
  limit = 6,
}: {
  band: Band;
  rows: WorkRow[];
  density: Density;
  onOpen: (r: WorkRow) => void;
  onSendTo: (r: WorkRow) => void;
  onClear: (r: WorkRow) => void;
  limit?: number;
}) {
  const [all, setAll] = useState(false);
  if (rows.length === 0) return null;
  const shown = all ? rows : rows.slice(0, limit);
  const [label, hint] = BAND_LABEL[band];
  return (
    <section aria-label={label} className="mt-3">
      <div className={`mb-1.5 flex items-baseline justify-between px-0.5 text-[11px] font-bold uppercase tracking-[0.07em] ${band === 'overdue' ? 'text-st-danger-fg' : 'text-tcs-text-muted'}`}>
        <span>
          {label} · {rows.length}
        </span>
        <span className="font-medium normal-case tracking-normal text-tcs-text-muted">{hint}</span>
      </div>
      <div className="overflow-hidden rounded-xl border border-tcs-border bg-tcs-bg">
        {shown.map((r) => (
          <WorkRowView key={r.key} row={r} density={density} onOpen={onOpen} onSendTo={onSendTo} onClear={onClear} />
        ))}
        {rows.length > shown.length && (
          <button
            type="button"
            onClick={() => setAll(true)}
            className="block min-h-[44px] w-full border-t border-tcs-border text-[13px] text-tcs-accent-ink"
          >
            Show all {rows.length}
          </button>
        )}
      </div>
    </section>
  );
}

/** The bottom sheet both actions open (the approved demo's sheet). */
function Sheet({ label, onCancel, children }: { label: string; onCancel: () => void; children: ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end md:items-center md:justify-center" role="dialog" aria-modal="true" aria-label={label}>
      <button type="button" aria-label="Cancel" className="absolute inset-0 bg-tcs-scrim/60" {...overlayClickClose(onCancel)} />
      <div className="relative w-full rounded-t-2xl bg-tcs-bg p-4 pb-[max(1rem,env(safe-area-inset-bottom))] shadow-2xl md:max-w-md md:rounded-2xl">
        {children}
      </div>
    </div>
  );
}

const firstName = (name: string) => name.split(' ')[0] || name;

export function ClearSheet({
  row,
  busy,
  error,
  onCancel,
  onConfirm,
}: {
  row: WorkRow;
  busy: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: (why: string) => void;
}) {
  const [why, setWhy] = useState('');
  return (
    <Sheet label="Clear from your list" onCancel={onCancel}>
      <h2 className="text-[15.5px] font-semibold text-tcs-text">Clear from your list?</h2>
      <p className="mt-1 text-[13px] text-tcs-text-secondary">
        {row.name} — “{row.missing}”
      </p>
      <p className="mt-1.5 text-[13.5px] leading-relaxed text-tcs-text-secondary">
        Takes it off your list only. Anyone else it was sent to still has it.
      </p>
      <input
        value={why}
        onChange={(e) => setWhy(e.target.value)}
        placeholder="Why? (optional)"
        aria-label="Why? (optional)"
        className="mt-2.5 block min-h-[44px] w-full rounded-lg border border-tcs-border bg-tcs-field px-3 text-[16px] text-tcs-text placeholder:text-tcs-text-muted"
      />
      {error && <p className="mt-2 rounded-md bg-st-danger-bg px-3 py-2 text-[13px] text-st-danger-fg">{error}</p>}
      <button
        type="button"
        disabled={busy}
        onClick={() => onConfirm(why)}
        className="mt-3 min-h-[44px] w-full rounded-lg bg-tcs-accent-ink text-[14px] font-semibold text-tcs-accent-on disabled:opacity-60"
      >
        {busy ? 'Clearing…' : 'Clear'}
      </button>
      <button type="button" onClick={onCancel} className="mt-1 min-h-[44px] w-full text-[14px] font-semibold text-tcs-accent-ink">
        Cancel
      </button>
    </Sheet>
  );
}

export function SendToSheet({
  row,
  myEid,
  people,
  peopleLoading,
  busy,
  error,
  onCancel,
  onConfirm,
}: {
  row: WorkRow;
  myEid: string | null;
  people: Person[];
  peopleLoading: boolean;
  busy: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: (to: Person, why: string) => void;
}) {
  const prefill = useMemo(() => sendToDefault(row, myEid, people), [row, myEid, people]);
  const [toEid, setToEid] = useState<string>('');
  const [why, setWhy] = useState('');
  const chosen = toEid || prefill?.employee_id || '';
  const options = people.filter((p) => p.employee_id !== myEid);
  if (prefill && !options.some((p) => p.employee_id === prefill.employee_id)) options.unshift(prefill);
  const to = options.find((p) => p.employee_id === chosen) ?? null;
  const roleWord = (r: string | null) => seatWord(r);
  return (
    <Sheet label="Send to" onCancel={onCancel}>
      <h2 className="text-[15.5px] font-semibold text-tcs-text">Send to…</h2>
      <p className="mt-1 text-[13px] text-tcs-text-secondary">
        {row.name} — “{row.missing}”
      </p>
      <p className="mt-1.5 text-[13.5px] leading-relaxed text-tcs-text-secondary">
        {prefill
          ? row.amEid && prefill.employee_id === row.amEid
            ? <>Prefilled: <b className="text-tcs-text">{prefill.name}</b>, who manages this building. Change it if it should go to someone else.</>
            : <>Prefilled: <b className="text-tcs-text">{prefill.name}</b>{roleWord(prefill.app_role) ? ` (${roleWord(prefill.app_role)})` : ''}. Change it if it should go to someone else.</>
          : 'No obvious owner here, so pick who.'}{' '}
        It leaves your list and goes on theirs; they are told you sent it and why.
      </p>
      <select
        value={chosen}
        onChange={(e) => setToEid(e.target.value)}
        disabled={peopleLoading}
        aria-label="Send to"
        className="mt-2.5 block min-h-[44px] w-full rounded-lg border border-tcs-border bg-tcs-field px-3 text-[16px] text-tcs-text"
      >
        <option value="">{peopleLoading ? 'Loading…' : 'Pick a person…'}</option>
        {options.map((p) => (
          <option key={p.employee_id} value={p.employee_id}>
            {p.name}
            {roleWord(p.app_role) ? ` · ${roleWord(p.app_role)}` : ''}
          </option>
        ))}
      </select>
      <input
        value={why}
        onChange={(e) => setWhy(e.target.value)}
        placeholder="Why? (optional)"
        aria-label="Why? (optional)"
        className="mt-2 block min-h-[44px] w-full rounded-lg border border-tcs-border bg-tcs-field px-3 text-[16px] text-tcs-text placeholder:text-tcs-text-muted"
      />
      {error && <p className="mt-2 rounded-md bg-st-danger-bg px-3 py-2 text-[13px] text-st-danger-fg">{error}</p>}
      <button
        type="button"
        disabled={busy || !to}
        onClick={() => to && onConfirm(to, why)}
        className="mt-3 min-h-[44px] w-full rounded-lg bg-tcs-accent-ink text-[14px] font-semibold text-tcs-accent-on disabled:opacity-60"
      >
        {busy ? 'Sending…' : to ? `Send to ${firstName(to.name)}` : 'Send'}
      </button>
      <button type="button" onClick={onCancel} className="mt-1 min-h-[44px] w-full text-[14px] font-semibold text-tcs-accent-ink">
        Cancel
      </button>
    </Sheet>
  );
}

/** Done today, with Undo where the change can be put back through the same door. */
export function DoneTodayStrip({ done, onUndo }: { done: DoneItem[]; onUndo: (d: DoneItem) => void }) {
  if (done.length === 0) return null;
  return (
    <div className="mt-2.5 rounded-xl border border-dashed border-st-ok-border bg-st-ok-bg px-3 py-1.5 text-[12.5px] text-st-ok-fg">
      <div className="font-bold">Done today · {done.length}</div>
      {done.map((d) => (
        <div key={d.key} className="flex min-h-[36px] items-center justify-between gap-2">
          <span className="min-w-0">{d.label}</span>
          {d.projectId || d.undo ? (
            <button type="button" onClick={() => onUndo(d)} className="min-h-[36px] shrink-0 font-bold underline">
              Undo
            </button>
          ) : null}
        </div>
      ))}
    </div>
  );
}
